/**
 * ACS B23025 employment rate (employed ÷ civilian labor force) and California peer
 * benefit percentile for the Climate Equity Index. Source: `acs5_unemployment`
 * (b23025_003e = labor force, b23025_005e = unemployed).
 *
 * Benefit: higher employment rate → higher percentile (better).
 */

import { supabaseClient } from './supabase';

const TARGET_YEAR = 2023;

type UnemploymentRow = {
  geofips?: string | number | null;
  geography_type?: string | null;
  year?: number | null;
  b23025_003e?: number | string | null;
  b23025_005e?: number | string | null;
};

function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function normalizeGeoType(g: string | null | undefined): string {
  return String(g ?? '').trim();
}

function canonicalGeofips(g: string | number | null | undefined): string | null {
  if (g === null || g === undefined) return null;
  const s = String(g).trim();
  if (!s) return null;
  if (!/^\d+$/.test(s)) return s;
  if (s.length === 6) return `0${s}`;
  if (s.length === 4) return `0${s}`;
  if (s.length === 1) return `0${s}`;
  return s;
}

function geofipsLookupVariants(geofips: number): string[] {
  const s = String(geofips).trim();
  const out = new Set<string>([s]);
  const c = canonicalGeofips(s);
  if (c) out.add(c);
  if (/^\d+$/.test(s)) {
    const u = s.replace(/^0+/, '') || '0';
    out.add(u);
  }
  return Array.from(out);
}

function placeKey(geofipsCanon: string, geographyType: string): string {
  return `${geofipsCanon}|${geographyType}`;
}

function employmentRateForPlaceRows(rows: UnemploymentRow[]): number | null {
  let labor = 0;
  let unemployed = 0;
  let any = false;
  for (const r of rows) {
    const L = toNum(r.b23025_003e);
    const U = toNum(r.b23025_005e);
    if (L == null || L <= 0) continue;
    labor += L;
    unemployed += U ?? 0;
    any = true;
  }
  if (!any || labor <= 0) return null;
  return Math.min(100, Math.max(0, (100 * (labor - unemployed)) / labor));
}

/** Higher rate → higher benefit percentile (0–100). */
function peerBenefitPercentile(rate: number, allRates: number[]): number {
  const n = allRates.length;
  if (n < 2) return 50;
  const less = allRates.filter((x) => x < rate).length;
  const equal = allRates.filter((x) => x === rate).length;
  const midRank = less + (equal - 1) / 2;
  return Math.min(100, Math.max(0, Math.round((midRank / (n - 1)) * 100)));
}

type DistCache = {
  year: number;
  benefitPercentileByKey: Map<string, number>;
};

let distCache: DistCache | null = null;

async function paginateUnemploymentYear(year: number, selectCols: string): Promise<UnemploymentRow[]> {
  const pageSize = 1000;
  const all: UnemploymentRow[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabaseClient
      .from('acs5_unemployment')
      .select(selectCols)
      .eq('year', year)
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const chunk = (data || []) as UnemploymentRow[];
    all.push(...chunk);
    if (chunk.length < pageSize) break;
  }
  return all;
}

async function buildDistribution(year: number): Promise<DistCache | null> {
  const selectCols = 'geofips, geography_type, year, b23025_003e, b23025_005e';
  let rows: UnemploymentRow[];
  try {
    rows = await paginateUnemploymentYear(year, selectCols);
  } catch (e) {
    console.warn('employmentRatePercentileService: failed to load acs5_unemployment', e);
    return null;
  }
  if (!rows.length) return null;

  const byPlace = new Map<string, UnemploymentRow[]>();
  for (const r of rows) {
    const canon = canonicalGeofips(r.geofips);
    const gt = normalizeGeoType(r.geography_type);
    if (!canon || !gt) continue;
    const key = placeKey(canon, gt);
    if (!byPlace.has(key)) byPlace.set(key, []);
    byPlace.get(key)!.push(r);
  }

  const rateByKey = new Map<string, number>();
  for (const [key, prs] of Array.from(byPlace.entries())) {
    const rate = employmentRateForPlaceRows(prs);
    if (rate != null) rateByKey.set(key, rate);
  }

  const allRates = Array.from(rateByKey.values());
  const benefitPercentileByKey = new Map<string, number>();
  for (const [key, rate] of Array.from(rateByKey.entries())) {
    benefitPercentileByKey.set(key, peerBenefitPercentile(rate, allRates));
  }

  return { year, benefitPercentileByKey };
}

async function getDistribution(year: number): Promise<DistCache | null> {
  if (distCache?.year === year) return distCache;
  const built = await buildDistribution(year);
  if (built) distCache = built;
  return built;
}

function lookupPercentile(
  geofips: number,
  geographyType: string,
  benefitPercentileByKey: Map<string, number>
): number | null {
  const gt = normalizeGeoType(geographyType);
  for (const v of geofipsLookupVariants(geofips)) {
    const canon = canonicalGeofips(v);
    if (!canon) continue;
    const key = placeKey(canon, gt);
    if (benefitPercentileByKey.has(key)) return benefitPercentileByKey.get(key)!;
  }
  return null;
}

function lookupPercentileWithGeoVariants(
  geofips: number,
  geographyType: string,
  benefitPercentileByKey: Map<string, number>
): number | null {
  const order = Array.from(
    new Set([geographyType, 'City', 'Place', 'County'].map((x) => normalizeGeoType(x)).filter(Boolean))
  );
  for (const gt of order) {
    const p = lookupPercentile(geofips, gt, benefitPercentileByKey);
    if (p != null) return p;
  }
  return null;
}

/**
 * Benefit percentile (0–100, higher = better employment vs peers) for the selected place.
 */
export async function fetchEmploymentRateBenefitPercentile(
  locationName: string,
  geofips: number | null,
  geographyType: string | null
): Promise<number | null> {
  if (!locationName) return null;

  let gf = geofips;
  let gt = geographyType;

  if (gf == null || !gt) {
    const { supabaseLocationService } = await import('./supabaseLocationService');
    const row = await supabaseLocationService.getGeofipsWithGeographyType(locationName);
    if (!row) return null;
    gf = row.geofips;
    gt = row.geography_type;
  }

  if (gf == null || !gt) return null;

  let year = TARGET_YEAR;
  let dist = await getDistribution(year);
  if (dist) {
    const p = lookupPercentileWithGeoVariants(gf, gt!, dist.benefitPercentileByKey);
    if (p != null) return p;
  }

  const variants = geofipsLookupVariants(gf);
  let latestY: number | null = null;
  for (const v of variants) {
    const { data } = await supabaseClient
      .from('acs5_unemployment')
      .select('year')
      .eq('geofips', v)
      .order('year', { ascending: false })
      .limit(1);
    const y = data?.[0]?.year;
    if (typeof y === 'number' && (latestY == null || y > latestY)) latestY = y;
  }
  if (latestY != null && latestY !== year) {
    distCache = null;
    dist = await getDistribution(latestY);
  }

  if (!dist) return null;
  return lookupPercentileWithGeoVariants(gf, gt!, dist.benefitPercentileByKey);
}
